import type { HeadToHead, InviteInfo, LeaderboardEntry, LeaderboardResponse, MatchSummary, ProfileResponse } from '@chess-uno/shared-protocol'
import { SERVER_URL, type StoredSession } from './session.js'

async function get<T>(path: string, session?: StoredSession | null): Promise<T | null> {
  const res = await fetch(`${SERVER_URL}${path}`, {
    headers: session ? { authorization: `Bearer ${session.token}` } : {},
  }).catch(() => null)
  if (!res?.ok) return null
  return (await res.json()) as T
}

export function fetchLeaderboard(): Promise<LeaderboardResponse | null> {
  return get<LeaderboardResponse>('/api/leaderboard')
}

export function fetchProfile(username: string, session?: StoredSession | null): Promise<ProfileResponse | null> {
  return get<ProfileResponse>(`/api/players/${encodeURIComponent(username)}`, session)
}

export function fetchHeadToHead(
  session: StoredSession,
  opponentId: string,
): Promise<{ headToHead: HeadToHead | null; me: LeaderboardEntry; opponent: LeaderboardEntry } | null> {
  return get(`/api/h2h/${encodeURIComponent(opponentId)}`, session)
}

export function fetchMatch(gameId: string): Promise<MatchSummary | null> {
  return get<MatchSummary>(`/api/matches/${encodeURIComponent(gameId)}`)
}

export function fetchInvite(code: string): Promise<InviteInfo | null> {
  return get<InviteInfo>(`/api/invites/${encodeURIComponent(code)}`)
}

/** Native share if available, otherwise copy to the clipboard. Returns what was done. */
export async function shareText(text: string, url?: string): Promise<'shared' | 'copied' | 'failed'> {
  const nav = navigator as Navigator & { share?: (d: { text?: string; url?: string; title?: string }) => Promise<void> }
  if (nav.share) {
    try {
      await nav.share({ title: 'Chess-Uno', text, ...(url ? { url } : {}) })
      return 'shared'
    } catch {
      // share cancelled: fall back to copying
    }
  }
  try {
    await navigator.clipboard.writeText(url ? `${text} ${url}` : text)
    return 'copied'
  } catch {
    return 'failed'
  }
}
